/**
 * Shared app chrome: the top bar (brand, channel switcher, tab nav, account
 * menu) that wraps every authed page.
 *
 * The active channel lives here (from `useChannels`) and is handed to the tabs
 * via the Outlet context, so switching it on one tab carries to the others. The
 * caller's role on the active channel rides along too, so pages can gate
 * streamer-only controls (chat ingestion, mod management).
 */
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { apiUrl, type Channel, type ChannelRole } from "../lib/api";
import { useChannels } from "../hooks/useChannels";
import { useTheme } from "../hooks/useTheme";
import { QueueSkeleton } from "./Skeleton";

// How long the "switching channels" cue stays up. Not tied to the actual
// fetch (each tab loads its own data independently) — just long enough to
// register that the click did something before the new tab content settles.
const SWITCH_CUE_MS = 700;

/** Shape shared with the tab routes via `useOutletContext`. */
export interface ChannelContext {
  channelId: string;
  setChannelId: (id: string) => void;
  channels: Channel[];
  /** The caller's role on the active channel (null while loading). */
  role: ChannelRole | null;
}

export function Layout() {
  const { channels, channelId, setChannelId, role, loading, error } = useChannels();
  const [theme, toggleTheme] = useTheme();

  const [switching, setSwitching] = useState(false);
  const switchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Mobile-only: Queue/Donations/Settings/StreamView + brightness/Account/Log
  // out collapse into a right-hand drawer (see .topbar-menu — display:contents
  // on desktop, so this state is inert there).
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  function handleChannelChange(id: string) {
    if (id !== channelId) {
      setSwitching(true);
      if (switchTimeout.current) clearTimeout(switchTimeout.current);
      switchTimeout.current = setTimeout(() => setSwitching(false), SWITCH_CUE_MS);
    }
    setChannelId(id);
  }

  useEffect(() => () => {
    if (switchTimeout.current) clearTimeout(switchTimeout.current);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeMenu();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <div className="app">
      <header className="topbar">
        <h1 className="brand">
          <img src="/brand/quelby-icon.svg" alt="" className="brand-mark" />
          <span>
            quel<span className="brand-b">b</span>y
          </span>
        </h1>

        <button
          type="button"
          className="hamburger-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          aria-controls="topbar-menu"
        >
          <span className="hamburger-icon" aria-hidden="true" />
        </button>

        {/* Channel switcher — your own channel plus any you're a mod on. */}
        {channels.length > 0 && (
          <label className={`channel-picker${switching ? " switching" : ""}`}>
            <span className="channel-picker-label">Channel</span>
            <select
              className="channel-switcher"
              value={channelId}
              onChange={(e) => handleChannelChange(e.target.value)}
              disabled={switching}
              aria-label="Active channel"
            >
              {channels.map((c) => (
                <option key={c.channelId} value={c.channelId}>
                  {c.displayName}
                  {c.role === "mod" ? " (mod)" : ""}
                </option>
              ))}
            </select>
            {switching && <span className="spinner channel-picker-spinner" aria-hidden="true" />}
          </label>
        )}

        {menuOpen && (
          <div className="topbar-menu-backdrop" onClick={closeMenu} aria-hidden="true" />
        )}

        <div id="topbar-menu" className={`topbar-menu${menuOpen ? " open" : ""}`}>
          <button
            type="button"
            className="topbar-menu-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            ×
          </button>

          <nav className="tabs">
            <NavLink to="/queue" className="tab" onClick={closeMenu}>
              Queue
            </NavLink>
            <NavLink to="/donations" className="tab" onClick={closeMenu}>
              Donations
            </NavLink>
            <NavLink to="/streamview" className="tab" onClick={closeMenu}>
              StreamView
            </NavLink>
            <NavLink to="/settings" className="tab" onClick={closeMenu}>
              Settings
            </NavLink>
          </nav>

          <div className="spacer" />

          <button
            type="button"
            className="btn ghost icon"
            onClick={() => {
              toggleTheme();
              closeMenu();
            }}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>

          <NavLink to="/account" className="btn ghost" onClick={closeMenu}>
            Account
          </NavLink>

          <form action={apiUrl("/auth/logout")} method="post">
            <button className="btn ghost" type="submit">
              Log out
            </button>
          </form>
        </div>
      </header>

      {loading && channels.length === 0 ? (
        <QueueSkeleton />
      ) : (
        <div className={`main-content${switching ? " switching" : ""}`}>
          {error && (
            <div className="error" style={{ marginBottom: 16 }}>
              Couldn’t load your channels — {error}
            </div>
          )}
          {!loading && !error && channels.length === 0 && (
            <div className="empty" style={{ marginBottom: 16 }}>
              You don’t have access to any channels yet. Ask a streamer to add you
              as a mod, or get your account whitelisted.
            </div>
          )}
          {/* /account is account-level and renders fine with no channel. */}
          <Outlet
            context={{ channelId, setChannelId, channels, role } satisfies ChannelContext}
          />
        </div>
      )}

      <span className="app-version" aria-hidden="true">
        {__APP_VERSION__}
      </span>
    </div>
  );
}
